import { Component, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { SettingService } from '../../../services/setting.service';
import { UserRole, getLoggedInUserRole } from '../../../utils/user-role.util';
import { RestaurantService } from '../../../services/restaurant.service';
import { FEATURE_FLAG_KEYS, FeatureFlag, isFeatureActive } from '../../../utils/feature-flag.util';
import { POS_PROVIDER } from '../../../utils/pos-provider.util';
import { isRouteAllowedForPlan } from '../../../utils/subscription-plan-menu.util';
import { TogoSettingComponent } from '../togo-setting/togo-setting.component';
import { PaymentSettingComponent } from '../payment-setting/payment-setting.component';

/**
 * One switch on the Settings page. `settingType` is the
 * `/restaurants/settings` row the flag lives in and `key` its
 * `settings_config` key — the same spelling each toggle body names it by:
 * `order_type` for the order row, `setting_key` for the feature row.
 */
export interface RestaurantSettingRow {
  settingType: 'order' | 'feature';
  key: string;
  title: string;
  description: string;
  icon: string;
}

/**
 * How the restaurant's plan and POS gate one switch (FNF-1224), from the
 * settings row's `availability`. Every switch is shown; `eligible` false locks
 * it off, and `reason` says why: `pos_not_selected` (no POS chosen yet — go to
 * Hardware), `pos` (Dine-In needs Fork & Find or Clover — go to Hardware) or
 * `plan` (move to `required_plan` — go to Billing). `visible` is what older
 * services sent to hide a row; a row is still dropped when it is false.
 */
export type SettingLockReason = 'plan' | 'pos' | 'pos_not_selected';

export interface SettingAvailability {
  visible: boolean;
  eligible: boolean;
  reason: SettingLockReason | null;
  required_plan: string | null;
}

export const RESTAURANT_SETTING_ROWS: RestaurantSettingRow[] = [
  {
    settingType: 'order',
    key: 'dine_in',
    title: 'Dine-In',
    description: 'Take orders from guests seated at your tables.',
    icon: 'bx-dish',
  },
  {
    settingType: 'order',
    key: 'online',
    title: 'Online Ordering',
    description: 'Let customers order ahead from the Fork & Find app.',
    icon: 'bx-globe',
  },
  {
    settingType: 'feature',
    key: 'kds',
    title: 'Kitchen Display (KDS)',
    description: 'Send tickets to kitchen display screens instead of printers.',
    icon: 'bx-desktop',
  },
];

/**
 * Restaurant → Settings: the restaurant-wide Dine-In, Online Ordering and
 * Kitchen Display switches, mirroring dashboard-app-mobile's Settings screen.
 *
 * Each switch commits as it is flipped — the flags are independent. The
 * switch moves at once and moves back, with an error toast, if the service
 * refuses. Only a restaurant admin may change them (the service refuses
 * every other role on both toggles); anyone else sees them disabled.
 *
 * The restaurant's plan and POS decide which switches can be on (FNF-1224);
 * the service sends that per switch as `availability` and enforces it. A
 * switch out of reach shows off and locked, with why and a link to where it
 * is fixed: Hardware for a POS reason, Billing for a plan reason. An older
 * service without `availability` gates nothing.
 *
 * Payments belongs to Fork & Find restaurants on Standard and above: Square
 * and Clover own their payment methods, and Free does not include them.
 */
@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [RouterLink, NgTemplateOutlet, TogoSettingComponent, PaymentSettingComponent],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css',
})
export class SettingsComponent {
  private settingService = inject(SettingService);
  private toastr = inject(ToastrService);
  private restaurantService = inject(RestaurantService);

  readonly allRows = RESTAURANT_SETTING_ROWS;
  readonly canEdit = getLoggedInUserRole() === UserRole.restaurantAdmin;

  loading = true;
  loadError = '';
  /** Flags whose settings row exists. A flag without its row is not "off" —
   * both toggles 404 when the row is missing — so it shows as unavailable. */
  available = new Set<string>();
  enabled = new Map<string, boolean>();
  availability = new Map<string, SettingAvailability>();
  /** Flags with a toggle in flight; their switches hold still until it lands. */
  pending = new Set<string>();

  /**
   * To Go and Payments used to be pages of their own; they now sit in this
   * page's Order and Payment sections. Each is only placed where its own page
   * was reachable — both components send the user home when they are not —
   * so the rules that hid them in the sidebar decide here too.
   */
  showTogo = false;
  /** Dine-In / Online / KDS switches, behind RESTAURANT_SETTINGS as before. */
  showSwitches = false;
  /** To Go is Standard-and-above, as its page was: on Free it shows locked
   * with an upgrade link instead of its controls. */
  togoLocked = false;
  private posProvider: string | null = POS_PROVIDER.FNF;
  private planName: string | null = null;

  /** Payments: Fork & Find POS on a Standard-and-above plan, else not shown. */
  get showPayment(): boolean {
    return (
      (this.posProvider || POS_PROVIDER.FNF).toLowerCase() === POS_PROVIDER.FNF &&
      isRouteAllowedForPlan('payment', this.planName)
    );
  }

  /** Nothing in any section applies to this restaurant. */
  get nothingToShow(): boolean {
    return !this.showSwitches && !this.showTogo && !this.showPayment;
  }

  ngOnInit() {
    this.load();
    this.restaurantService.restaurantFeatureFlags$.subscribe((flags: FeatureFlag[] | null) => {
      this.showTogo = isFeatureActive(flags, FEATURE_FLAG_KEYS.TO_GO);
      this.showSwitches = isFeatureActive(flags, FEATURE_FLAG_KEYS.RESTAURANT_SETTINGS);
    });
    this.restaurantService.restaurantPosProvider$.subscribe((provider: any) => {
      this.posProvider = provider;
    });
    this.restaurantService.currentPlanName$.subscribe((plan: string | null) => {
      this.planName = plan;
      this.togoLocked = !isRouteAllowedForPlan('togo', plan);
    });
  }

  /** Dine-In and Online Ordering. */
  get orderRows(): RestaurantSettingRow[] {
    return this.rows.filter((row) => row.settingType === 'order');
  }

  /** Kitchen Display and any later feature switch. */
  get featureRows(): RestaurantSettingRow[] {
    return this.rows.filter((row) => row.settingType === 'feature');
  }

  load() {
    this.loading = true;
    this.loadError = '';
    this.settingService.getSettings().subscribe((response: any) => {
      this.loading = false;
      // A restaurant with no settings rows at all answers 404: an empty page,
      // not a failure.
      if (response?.status === 404) {
        this.applyRows([]);
        return;
      }
      if (!response?.data) {
        this.loadError =
          response?.error?.error?.message ||
          response?.error?.message ||
          'Unable to load settings. Please try again later.';
        return;
      }
      this.applyRows(response.data);
      this.settingService.currentRestaurantSettings$.next(response.data);
    });
  }

  /** Every switch; only an older service's `visible: false` drops one. */
  get rows(): RestaurantSettingRow[] {
    return this.allRows.filter((row) => this.availability.get(row.key)?.visible !== false);
  }

  get isEmpty(): boolean {
    return (
      !this.loading && !this.loadError && !this.rows.some((row) => this.available.has(row.key))
    );
  }

  /** Why this switch is out of reach, or null when it can be flipped. An
   * older service's lock without a reason is a plan lock. */
  lockReason(row: RestaurantSettingRow): SettingLockReason | null {
    const found = this.availability.get(row.key);
    if (!found || found.eligible) return null;
    return found.reason || 'plan';
  }

  /** The plan that unlocks this switch, or null when it is not plan-locked. */
  requiredPlan(row: RestaurantSettingRow): string | null {
    if (this.lockReason(row) !== 'plan') return null;
    return this.availability.get(row.key)?.required_plan || 'Standard';
  }

  isLocked(row: RestaurantSettingRow): boolean {
    return this.lockReason(row) !== null;
  }

  /** On as it applies: a locked switch is off whatever is stored. */
  isOn(row: RestaurantSettingRow): boolean {
    return !this.isLocked(row) && (this.enabled.get(row.key) ?? false);
  }

  isAvailable(row: RestaurantSettingRow): boolean {
    return this.available.has(row.key);
  }

  isDisabled(row: RestaurantSettingRow): boolean {
    return (
      !this.canEdit || !this.isAvailable(row) || this.isLocked(row) || this.pending.has(row.key)
    );
  }

  toggle(row: RestaurantSettingRow, event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.checked;
    if (this.isDisabled(row)) {
      input.checked = !value;
      return;
    }
    this.pending.add(row.key);
    this.enabled.set(row.key, value);

    const request =
      row.settingType === 'feature'
        ? this.settingService.toggleFeatureSetting({ setting_key: row.key, value })
        : this.settingService.editSettings({ order_type: row.key, value });

    request.subscribe((response: any) => {
      this.pending.delete(row.key);
      if (!response?.data) {
        this.enabled.set(row.key, !value);
        input.checked = !value;
        this.toastr.error(
          response?.error?.error?.detail?.message ||
            response?.error?.error?.message ||
            response?.error?.message ||
            'Unable to complete your action. Please try again later.',
          'Setting Not Saved',
        );
        return;
      }
      this.syncSharedSettings(row, value);
      this.toastr.success(`${row.title} turned ${value ? 'on' : 'off'}`, 'Success');
    });
  }

  private applyRows(rows: any[]) {
    this.available.clear();
    this.enabled.clear();
    this.availability.clear();
    for (const entry of rows || []) {
      const config = entry?.settings_config;
      if (!config || typeof config !== 'object') continue;
      for (const row of this.allRows) {
        if (entry.setting_type !== row.settingType) continue;
        this.available.add(row.key);
        this.enabled.set(row.key, config[row.key]?.enabled === true);
        const gate = entry.availability?.[row.key];
        if (gate) this.availability.set(row.key, gate);
      }
    }
  }

  /** Keeps the shared settings other pages (To Go, the sidebar) read in step. */
  private syncSharedSettings(row: RestaurantSettingRow, value: boolean) {
    const current = this.settingService.currentRestaurantSettings$.value;
    if (!Array.isArray(current)) return;
    this.settingService.currentRestaurantSettings$.next(
      current.map((entry: any) =>
        entry?.setting_type === row.settingType
          ? {
              ...entry,
              settings_config: {
                ...entry.settings_config,
                [row.key]: { ...(entry.settings_config?.[row.key] || {}), enabled: value },
              },
            }
          : entry,
      ),
    );
  }
}
